<script setup>
import ProductPriceDetailsTable from './shared/ProductPriceDetailsTable.vue'
import BatchPriceUpdateDialog from '../products-page/BatchPriceUpdateDialog.vue'
import BaseProductSelect from '../base/BaseProductSelect.vue'
import PricingFilterCard from './shared/PricingFilterCard.vue'
import PricingStatusSelect from './shared/PricingStatusSelect.vue'
import PricingCountChip from './shared/PricingCountChip.vue'
import SpecialPricingChipsCell from './shared/SpecialPricingChipsCell.vue'
import BasePerPageSelect from '../base/BasePerPageSelect.vue'
import BasePagination from '../base/BasePagination.vue'

import { useStore } from '@/utils/VuexHelpers'
import { computed, ref } from 'vue'
import { formatCurrency } from '@/helpers/stringHelpers.js'

const batchPriceDialogRef = ref(null)
const expandedProducts = ref([])
const selectedProducts = ref([])
const isBatchPriceDialogOpen = ref(false)

const store = useStore()
const specific = computed(
  () => store.getters.filtersSpecialPricingProducts.specific.value
)
const specialPricingProducts = computed(
  () => store.getters.specialPricingProducts
)
const totalSpecialPricingProducts = computed(
  () => store.getters.totalSpecialPricingProducts
)
const loadingProd = computed(
  () => store.getters.loadingSpecialPricingProd === 'list'
)
const productNames = computed(() => store.getters.productNames || [])
const perPageSpecialPricingProducts = computed(
  () => store.getters.perPageSpecialPricingProducts
)
const pageNrSpecialPricingProducts = computed(
  () => store.getters.pageNrSpecialPricingProducts
)
const loadingProductNames = computed(
  () => store.getters.loadingProducts === 'names'
)

const allProducts = computed({
  get: () => {
    return specific.value !== 'true'
  },
  set: (value) => {
    store.commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'specific',
      value: value ? 'false' : 'true'
    })
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS', 0)
    store.dispatch('fetchSpecialPricingProducts')
  }
})

const productHeaders = [
  {
    text: '',
    value: 'data-table-expand',
    sortable: false,
    cellClass: 'product-expand'
  },
  {
    text: 'Product',
    value: 'product',
    sortable: true,
    cellClass: 'product-root-cell'
  },
  {
    text: 'Standard Price',
    value: 'default_price',
    align: 'end',
    sortable: false
  },
  {
    text: 'Customers with Special Pricing',
    value: 'prices',
    align: 'start',
    sortable: false,
    cellClass: 'pt-1'
  },
  {
    text: 'Prices Count',
    value: 'total_count',
    align: 'start',
    sortable: true
  }
]

const pricingStatus = computed({
  get: () => {
    return store.getters.filtersSpecialPricingProducts.prices.value || ''
  },
  set: (value) => {
    store.commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'prices',
      value
    })
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS', 0)
    store.dispatch('fetchSpecialPricingProducts')
  }
})

const perPage = computed({
  get: () => perPageSpecialPricingProducts.value,
  set: (value) => {
    store.commit('SET_PER_PAGE_SPECIAL_PRICING_PRODUCTS', value)
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS', 0)
    store.dispatch('fetchSpecialPricingProducts')
  }
})

const updatePageNr = (page) => {
  store.commit('SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS', page - 1)
  store.dispatch('fetchSpecialPricingProducts')
}

const productRows = computed(() => {
  return specialPricingProducts.value
})

const isProductExpanded = (item) => {
  return expandedProducts.value.some((expandedItem) => {
    return Number(expandedItem.id) === Number(item.id)
  })
}

const getProductRowClass = (item) => {
  return isProductExpanded(item) ? 'product-row-expanded' : ''
}

const handleBatchPriceSave = async (newPrices) => {
  if (!newPrices) return

  const { effectiveDate, endDate, comment, items, customerIds } = newPrices

  const payload = customerIds.length
    ? customerIds.flatMap((customerId) => {
        return items.map((price) => ({
          productlist_id: price.productId,
          customer_id: customerId,
          start_date: effectiveDate,
          end_date: !endDate || endDate === '' ? null : endDate,
          price_kg: price.newPriceKg,
          comment
        }))
      })
    : items.map((price) => ({
        productlist_id: price.productId,
        start_date: effectiveDate,
        price_kg: price.newPriceKg,
        end_date: !endDate || endDate === '' ? null : endDate,
        comment
      }))

  const res = await store.dispatch('batchCreateProdPrice', { payload })
  if (res === 'error') return

  batchPriceDialogRef.value?.resetForm()
  isBatchPriceDialogOpen.value = false
  store.dispatch('fetchSpecialPricingPageData')
}

const pricesWithCustomer = (prices = []) => {
  return prices.filter((price) => {
    return price.customer_id !== null && price.customer_id !== undefined
  })
}

const selectedProductFilter = computed({
  get: () => {
    const code = store.getters.filtersSpecialPricingProducts.code.value
    if (!code) return null

    const product =
      productNames.value.find((product) => {
        return String(product.code) === String(code)
      }) || null
    return product
  },
  set: (product) => {
    store.commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'code',
      value: product?.code || ''
    })
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS', 0)
    store.dispatch('fetchSpecialPricingProducts')
  }
})
</script>

<template>
  <v-tab-item value="product" class="pa-2">
    <PricingFilterCard>
      <v-row dense align="center">
        <v-col cols="12" md="auto">
          <div class="d-flex align-center">
            <span class="filter-label mr-2">Selected</span>

            <v-chip small label outlined class="selected-count-chip">
              {{ selectedProducts.length }}
            </v-chip>
          </div>
        </v-col>

        <v-col cols="12" md="auto">
          <v-btn
            small
            color="info"
            :disabled="selectedProducts.length === 0"
            @click="isBatchPriceDialogOpen = true"
          >
            <font-awesome-icon :icon="['fas', 'edit']" class="mr-1" />
            Batch update prices
          </v-btn>
        </v-col>

        <v-spacer />

        <v-col cols="12" md="auto">
          <div class="d-flex align-center">
            <BasePerPageSelect
              v-model="perPage"
              class="mr-3"
              :label="'Products per page'"
            />
            <PricingCountChip
              label="Total products"
              :count="totalSpecialPricingProducts"
            />
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <!-- Second row: filters -->
      <v-row dense align="center">
        <v-col cols="12" md="4" lg="3">
          <BaseProductSelect
            label="Product"
            :product="selectedProductFilter"
            clearable
            dense
            outlined
            hide-details
            @update:product="selectedProductFilter = $event"
          />
        </v-col>

        <v-col cols="12" md="3" lg="2">
          <PricingStatusSelect v-model="pricingStatus" />
        </v-col>

        <v-col cols="12" md="auto">
          <v-switch
            v-model="allProducts"
            inset
            dense
            hide-details
            label="Show all products"
            class="mt-0 ml-md-2"
          />
        </v-col>
      </v-row>

      <BatchPriceUpdateDialog
        ref="batchPriceDialogRef"
        v-model="isBatchPriceDialogOpen"
        :selected-products="selectedProducts"
        :customer-required="true"
        @save="handleBatchPriceSave"
      />
    </PricingFilterCard>

    <v-data-table
      :headers="productHeaders"
      :items="productRows"
      v-model="selectedProducts"
      :show-select="true"
      item-key="id"
      show-expand
      height="800"
      fixed-header
      sort-by="code"
      :sort-desc="false"
      :expanded.sync="expandedProducts"
      :loading="loadingProd || loadingProductNames"
      loading-text="Loading product special pricing..."
      class="elevation-1 product-pricing-table"
      :item-class="getProductRowClass"
      :server-items-length="totalSpecialPricingProducts"
      hide-default-footer
    >
      <template #item.product="{ item }">
        <v-row no-gutters class="flex-column">
          <div class="font-weight-bold text-no-wrap">
            {{ item.product }}
          </div>

          <small v-if="item.code" class="font-italic">
            <b class="text-muted">Code:</b>
            <span class="text-overline"> {{ item.code }}</span>
          </small>
        </v-row>
      </template>

      <template #item.default_price="{ value }">
        {{ formatCurrency(value) }}
      </template>

      <template #item.prices="{ value }">
        <SpecialPricingChipsCell
          :display-type="'customer'"
          :prices="pricesWithCustomer(value)"
          :max-name-length="12"
          :max-visible="6"
        />
      </template>

      <template #item.total_count="{ item }">
        {{ pricesWithCustomer(item.prices).length || 0 }}
      </template>

      <template #expanded-item="{ item }">
        <td colspan="2"></td>

        <td
          :colspan="productHeaders.length - 1"
          class="expanded-row-cell blue-left-border pa-4"
        >
          <div class="expanded-row-wrapper">
            <ProductPriceDetailsTable
              :prices="item.prices"
              :product="item"
              :allow-customer-select="true"
              @refresh="store.dispatch('fetchSpecialPricingPageData')"
            />
          </div>
        </td>
      </template>
    </v-data-table>
    <BasePagination
      :total="totalSpecialPricingProducts"
      :per-page="perPageSpecialPricingProducts"
      :current-page="pageNrSpecialPricingProducts + 1"
      item-label="products"
      :disabled="loadingProd"
      @update:page="updatePageNr"
      :show="true"
    />
  </v-tab-item>
</template>

<style scoped>
.filter-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.04em;
}

.filter-switch {
  min-width: 180px;
}

.selected-count-chip.v-chip.v-chip--outlined.v-chip {
  background: #fff !important;
  background-color: #fff !important;
}

:deep(.pricing-filter-card .v-btn) {
  text-transform: none;
  letter-spacing: 0;
}

:deep(.pricing-filter-card .v-input--selection-controls) {
  margin-top: 0;
  padding-top: 0;
}
:deep(.pricing-filter-card .v-input--selection-controls .v-label) {
  margin-bottom: 0;
}
</style>
